import { FormControl } from 'react-bootstrap';
import { useForm, SubmitHandler } from 'react-hook-form';
import { ensureUrlHasProtocol, generateInitName, validateUrl } from '../utils/logic';
import { Shortlink, ShortlinkType } from '../models/shared/Shortlink';
import React from 'react';
import { createShortlink, updateShortlink } from '../utils/api';
import { Collections, ERROR_KNOWN_THREAD, KNOWN_THREAD_WARNING } from '../utils/shared/constants';
import { Box, CustomModalLayout, DatePicker, FormField, Loader, Modal, SectionHeader, TextButton, TimeInput } from '@wix/design-system';
import { useFirestore } from 'reactfire';
import { collection, getDocs, query, where } from 'firebase/firestore';

const RICK_ROLL = 'https://www.youtube.com/watch?v=dQw4w9WgXcQ';

interface Inputs {
    alias: string;
    name: string
    targetUrl: string;
}

interface Props {
    show: boolean;
    onHide: () => void;
    shortlink?: Shortlink;
    type: ShortlinkType
    onTypeChange?: (type: ShortlinkType) => void;
}

export const WriteShortlinkModal = ({ onHide, shortlink, type, onTypeChange, show }: Props) => {
    const [processing, setProcessing] = React.useState(false);
    const { register, handleSubmit, formState: { errors } } = useForm<Inputs>({
        defaultValues: {
            alias: shortlink?.alias || shortlink?.id || '',
            targetUrl: shortlink?.targetUrl || '',
            name: shortlink?.name || generateInitName(),
        }
    });
    const [activeFrom, setActiveFrom] = React.useState<Date | null>(shortlink?.activeFrom ? new Date(shortlink.activeFrom) : null);
    const [activeTo, setActiveTo] = React.useState<Date | null>(shortlink?.activeTo ? new Date(shortlink.activeTo) : null);
    const firestore = useFirestore();
    const creatingNew = !shortlink;
    const isStatic = type === ShortlinkType.STATIC;

    const onSubmit: SubmitHandler<Inputs> = async (data) => {
        setProcessing(true);
        const shortlinkData: Partial<Shortlink> = {
            targetUrl: ensureUrlHasProtocol(data.targetUrl),
            name: data.name,
            alias: data.alias,
            activeFrom,
            activeTo,
            type
        }


        try {

            if (shortlink?.id) {
                await updateShortlink(shortlink.id, shortlinkData);
            } else {
                await createShortlink(shortlinkData);
            }
        } catch (e) {
            const message = (e as any).message || 'An error occurred';
            if (message.includes(ERROR_KNOWN_THREAD)) {
                alert(KNOWN_THREAD_WARNING);
            } else {
                console.log('error', e)
            }
        }

        setProcessing(false);
        onHide();
    };

    const isAliasValid = async (value: string) => {
        if (value === shortlink?.id) {
            return true;
        }
        if (value === shortlink?.alias) {
            return true;
        }
        if (creatingNew && !value) {
            return true;
        }

        const q = query(collection(firestore, Collections.shortlinks), where('alias', '==', value));
        const snap = await getDocs(q);

        if (snap.size === 0) {
            return true;
        }

        return 'taken';
    }

    const aliasTaken = errors.alias?.message === 'taken';

    let title = 'New QR Code';
    let subtitle = 'Create a professional dynamic QR Code you can update anytime - without changing the code itself. Track scans with built-in analytics.';
    if (shortlink) {
        title = 'Update QR Code';
        subtitle = 'Update your QR Code details instantly—no need to change or reprint the code.';
    } else if (type === ShortlinkType.STATIC) {
        title = 'New Static QR Code';
        subtitle = 'A simple, fixed QR Code without editing or analytics. Works forever, but cannot be updated later.';
    }

    return (
        <Modal onRequestClose={onHide} isOpen={show}>
            <CustomModalLayout
                primaryButtonText="Save"
                primaryButtonProps={{ prefixIcon: processing ? <div className='me-2'><Loader size="tiny" /></div> : undefined }}
                secondaryButtonText="Cancel"
                onCloseButtonClick={onHide}
                title={title}
                subtitle={subtitle}
                secondaryButtonOnClick={onHide}
                primaryButtonOnClick={handleSubmit(onSubmit)}
                removeContentPadding
                width={800}
                content={
                    <form onSubmit={handleSubmit(onSubmit)} >
                        <Box direction='vertical' gap="SP4">


                            <Box direction="vertical" gap={'SP4'} paddingLeft={'SP5'} paddingRight={'SP5'}>
                                {creatingNew &&
                                    <Box align='right'>
                                        <TextButton
                                            onClick={() => onTypeChange?.(isStatic ? ShortlinkType.DYNAMIC : ShortlinkType.STATIC)}
                                        >{`Switch to ${isStatic ? 'dynamic' : 'static'} QR Code`}</TextButton>
                                    </Box>}
                                <FormField label="Name"
                                    infoContent="Name is used for your own identification and is not shown externally"
                                >
                                    <FormControl
                                        isInvalid={!!errors.name}
                                        type='text'
                                        {...register('name', { required: true })}
                                    />
                                </FormField>


                                <FormField label="Target URL"
                                    infoContent={<div>
                                        Web address to create QR code for. This can be anything from Wix page, Google search result or <a href={RICK_ROLL} target='_blank' rel='noreferrer'>this</a>
                                    </div>}
                                    status='error'
                                    statusMessage={errors.targetUrl && (errors.targetUrl.message || 'Target URL is required')}
                                >
                                    <FormControl
                                        isInvalid={!!errors.targetUrl}
                                        type='text'
                                        {...register('targetUrl', { validate: validateUrl })}
                                    />
                                </FormField>

                            </Box>
                            <SectionHeader title="Advanced" />
                            <Box direction="vertical" gap={'SP4'} paddingLeft={'SP5'} paddingRight={'SP5'}>



                                <Box gap="SP4">
                                    <FormField label="Active From"
                                        infoContent={"The date and time from which the shortlink will be active. If not set, the shortlink is active immediately."}
                                        suffix={
                                            <TextButton
                                                disabled={isStatic} size="small" onClick={() => setActiveFrom(null)} >Unset</TextButton>
                                        }
                                    >
                                        <Box gap={2}>
                                            <DatePicker
                                                value={activeFrom ? activeFrom : undefined}
                                                onChange={(date: Date) => setActiveFrom(date || null)}
                                                placeholderText="Select date"
                                                disabled={isStatic}
                                            />
                                            <TimeInput
                                                disabled={isStatic}
                                                value={activeFrom}
                                                onChange={value => setActiveFrom(value?.date || null)}
                                            />
                                        </Box>
                                    </FormField>
                                    <FormField label="Active To"
                                        infoContent={"The date and time until which the shortlink will be active. If not set, the shortlink is active indefinitely."}
                                        suffix={
                                            <TextButton
                                                disabled={isStatic} size="small" onClick={() => setActiveTo(null)} >Unset</TextButton>
                                        }
                                    >
                                        <Box gap={2}>
                                            <DatePicker
                                                value={activeTo ? activeTo : undefined}
                                                onChange={(date: Date) => setActiveTo(date || null)}
                                                placeholderText="Select date"
                                                disabled={isStatic}
                                            />
                                            <TimeInput
                                                value={activeTo}
                                                onChange={value => setActiveTo(value?.date || null)}
                                                disabled={isStatic}
                                            />
                                        </Box>
                                    </FormField>


                                </Box>
                                <FormField label="Alias"
                                    infoContent={"Alias can be used in your short URL to have more human readable links"}
                                    statusMessage={aliasTaken ? 'Alias is already taken' : undefined}
                                    status={aliasTaken ? 'error' : undefined}
                                >
                                    <FormControl
                                        isInvalid={!!errors.alias}
                                        type='text'
                                        disabled={isStatic}
                                        {...register('alias', { required: !creatingNew, validate: isAliasValid })}
                                    />

                                </FormField>
                            </Box>

                        </Box>
                        <button type="submit" style={{ display: 'none' }}>Submit</button>
                    </form>
                }
            />
        </Modal>
    );

};
